import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { getQueryParamsWithCustomQueryObj } from "./../../utils/getQueryParamsFromCustomQueryObject";
import { permissionsEndpoints } from "./roles-permissions.endpoints";
import { rolesDirectoryClient } from "./rolesDirectory.client";
import type { AccessCatalogResponse } from "./accessCatalog.types";
import type {
  AllModulesResponse,
  AllRolesParams,
  AllRolesResponse,
  ModuleByCodeResponse,
  Role,
  RoleByIdResponse,
  RoleDeactivateImpactRead,
  RoleHoldersResponse,
  RoleScopeImpactRead,
} from "./roles-permissions.queries.types";
import type { ScopeBox } from "@/api/access/access.types";

export const PermissionsQueryKeys = {
  getAllModules: "allModules",
  getModuleByCode: "moduleByCode",
  getAccessCatalog: "accessCatalog",
  getAllRoles: "allRoles",
  getRoleById: "roleById",
  getRoleHolders: "roleHolders",
  getRoleDeactivateImpact: "roleDeactivateImpact",
  getRoleScopeImpact: "roleScopeImpact",
  getRoleDepartments: "roleDepartments",
} as const;

/**
 * Every switch a role can hold, in one request. `GET /user-management/permissions` serves only
 * view/create/edit and no page/tab kind, so it cannot describe the Access tab's model; it also
 * needs a `code` per module, which cost 22 requests.
 *
 * Quiet: every screen that reads it says itself when it fails, and on a person's record a reader
 * without `roles_permissions:view` is refused it by design (SERV-3576, D260 decision 9), which the
 * tab states inline rather than as an error toast.
 */
export const fetchAccessCatalog = async (): Promise<AccessCatalogResponse> => {
  const { data } = await axiosInstance.get(`${permissionsEndpoints.getAccessCatalog}`, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data;
};

type RoleDepartmentsResponse = { data?: Array<{ value?: unknown; label?: unknown }> | null };

/**
 * SERV-3551: the departments a role can carry in the request's company: the canonical ones, then
 * any other a role there was saved with. An entry without a string value is dropped.
 */
export type RoleDepartmentOption = { value: string; label: string };

export const fetchRoleDepartments = async (): Promise<RoleDepartmentOption[]> => {
  // Quiet: both role modals mount with their page, so a failure here would toast at someone who
  // never opened the field.
  const { data } = await axiosInstance.get<RoleDepartmentsResponse>(
    permissionsEndpoints.getRoleDepartments,
    { suppressErrorAlert: true } as CustomAxiosRequestConfig,
  );
  return (data?.data ?? []).flatMap((option) =>
    typeof option.value === "string" && option.value.trim()
      ? [
          {
            value: option.value,
            label: typeof option.label === "string" ? option.label : option.value,
          },
        ]
      : [],
  );
};

/**
 * Quiet, like the permission list: the role page, Compare, the assign permissions page and a
 * person's Effective Access tab each say themselves when it fails, and that tab answers a 403 as
 * "You don't have access" inline (SERV-3576).
 */
export const fetchAllModules = async (): Promise<AllModulesResponse> => {
  const { data } = await axiosInstance.get(`${permissionsEndpoints.getAllModules}`, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data;
};

export const getModuleByCode = async (
  code: string,
  search?: string,
): Promise<ModuleByCodeResponse> => {
  const params: Record<string, string> = { code };
  if (search) {
    params.search = search;
  }
  const { data } = await axiosInstance.get(
    `${permissionsEndpoints.getModuleByCode}?${getQueryParamsWithCustomQueryObj(params)}`,
  );
  return data;
};

export const fetchAllRoles = async ({
  search,
  sortBy,
  sortOrder,
  page,
  pageSize,
  filters,
  signal,
}: AllRolesParams): Promise<AllRolesResponse> => {
  const params = new URLSearchParams();

  // Only include page and pageSize if filters are not present
  if (!filters || filters.length === 0) {
    params.append("page", page?.toString() ?? "1");
    params.append("pageSize", pageSize?.toString() ?? "10");
  }

  if (search) {
    params.append("search", search);
  }

  if (sortBy) {
    params.append("sortBy", sortBy);
  }

  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }

  const filterBy = buildFilterByParam(filters, {
    keyMap: { roleName: "name", modules: "module" },
    getValue: (f) => (f.filterBy === "status" ? f.filterValue.value : f.filterValue.id),
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const { data } = await axiosInstance.get(`${permissionsEndpoints.getAllRoles}?${queryString}`, {
    signal,
  });
  return Array.isArray(data?.data)
    ? { ...data, data: data.data.map((role: Role) => rolesDirectoryClient.decorateListRole(role)) }
    : data;
};

export const fetchRoleById = async (
  id: string,
  config?: CustomAxiosRequestConfig,
): Promise<RoleByIdResponse> => {
  const { data } = await axiosInstance.get(`${permissionsEndpoints.getRoleById}/${id}`, config);
  return data?.data ? { ...data, data: rolesDirectoryClient.decorate(data.data) } : data;
};

export const fetchRoleHolders = async (
  roleId: string,
  { page, pageSize }: { page: number; pageSize: number },
  signal?: AbortSignal,
): Promise<RoleHoldersResponse> => {
  const params = getQueryParamsWithCustomQueryObj({
    page: String(page),
    pageSize: String(pageSize),
  });
  const config: CustomAxiosRequestConfig = { signal, suppressErrorAlert: true };
  const { data } = await axiosInstance.get(
    `${permissionsEndpoints.getRoleHolders(roleId)}?${params}`,
    config,
  );
  return data;
};

export const fetchRoleDeactivateImpact = async (
  roleId: string,
  signal?: AbortSignal,
): Promise<RoleDeactivateImpactRead> => {
  // Quiet: the Deactivate modal says itself when the check could not run.
  const config: CustomAxiosRequestConfig = { signal, suppressErrorAlert: true };
  const { data } = await axiosInstance.get<{ data?: RoleDeactivateImpactRead | null }>(
    permissionsEndpoints.getRoleDeactivateImpact(roleId),
    config,
  );
  // An answer without the impact is no answer: reading it as "nobody" would offer the deactivate.
  if (!data?.data) throw new Error("The deactivate-impact read answered without an impact");
  return data.data;
};

/** What a scope save would change that the scope-impact read checks (SERV-3592). */
export type RoleScopeImpactAsk = {
  /** The boxes going to Required from something else. */
  boxes: ScopeBox[];
  /** One entity only going from off to on. */
  oneEntityOnly: boolean;
};

export const fetchRoleScopeImpact = async (
  roleId: string,
  { boxes, oneEntityOnly }: RoleScopeImpactAsk,
  signal?: AbortSignal,
): Promise<RoleScopeImpactRead> => {
  const params: Record<string, string> = {};
  if (boxes.length > 0) params.boxes = boxes.join(",");
  if (oneEntityOnly) params.oneEntityOnly = "true";
  // Quiet: the Edit Scope modal says itself when the check could not run, and a backend without
  // the read answers 404, which the modal treats the same way.
  const config: CustomAxiosRequestConfig = { signal, suppressErrorAlert: true };
  const { data } = await axiosInstance.get<{ data?: RoleScopeImpactRead | null }>(
    `${permissionsEndpoints.getRoleScopeImpact(roleId)}?${getQueryParamsWithCustomQueryObj(params)}`,
    config,
  );
  // An answer without the impact is no answer: reading it as "nobody" would skip the warning.
  if (!data?.data) throw new Error("The scope-impact read answered without an impact");
  return data.data;
};

export const getRolesDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
): Promise<any> => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  // For roleName: /user-management/dropdown?type=roles
  // Response: {success: true, data: [...], total, page, pageSize, totalPages}
  if (key == "roleName") {
    params.append("type", "roles");
  }

  // For modules: /user-management/dropdown?type=module
  // Response: {success: true, data: [...], total, page, pageSize, totalPages}
  if (key == "modules") {
    params.append("type", "module");
  }

  // For status: /dropdown?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=role_status
  // Response: {success: true, data: [...], pagination: {...}}
  if (key == "status") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "role_status");
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  // Choose base URL once based on key and make a single HTTP call
  const baseUrl =
    key === "status" ? permissionsEndpoints.dropdown : permissionsEndpoints.getRolesDropdown;

  const { data } = await axiosInstance.get<any>(`${baseUrl}?${queryString}`, {
    signal,
  });

  // All variants return {success, data, ...}; ApiFilterChip reads .data
  return data;
};
